import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Room } from 'src/app/shared/models/room.model';

import { environment } from 'src/environments/environment';

import { UrlParametersService } from '../urlParameters/url-parameters.service';

interface DataLayerTracker {
  date?: string;
  startTime?: string;
  endTime?: string;
  capacity?: number;
  location?: string;
  city?: string;
  country?: string;
  centreName?: string;
  centreNumber?: number;
  price?: number;
  currency?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  protected readonly hostname: string = globalThis.location.hostname;
  protected dataLayer = globalThis['dataLayer'] || (globalThis['dataLayer'] = []);
  protected dataLayerRegus = globalThis['dataLayerRegus'] || (globalThis['dataLayerRegus'] = []);
  protected get decibelInsight(): ((...args: unknown[]) => void) | null {
    const fn = globalThis['decibelInsight'];
    return typeof fn === 'function' ? fn : null;
  }

  // default culture
  protected culture = 'en-GB';

  // transaction
  protected transactionProducts = [];
  protected transactionCurrencyCode;
  // protected transactionMainProductId;
  protected transactionMainProductPrice;
  protected transactionMainProductName;
  protected transactionMainProductUrlPart;
  protected transactionMainProductTax;
  protected transactionId;
  protected transactionTotal;
  private trackingUser = {};

  constructor(
    private readonly urlParamsService: UrlParametersService,
    private readonly http: HttpClient
  ) {}

  // add methods add service properties only
  addMainTransactionProduct(): void {
    this.transactionProducts.push({
      sku: 'MR',
      name: `${this.transactionMainProductName}`,
      category: 'Short Stay',
      price: `${this.transactionMainProductPrice}`,
      quantity: '1'
    });
  }

  addAdditionalService(service: any): void {
    this.transactionProducts.push({
      sku: `${service.id}`,
      name: `${service.name}`,
      category: 'Service',
      price: `${service.price}`,
      quantity: `${service.quantity}`
    });
  }

  addGeneralRoomTransactionData(room: Room): void {
    this.transactionCurrencyCode = room.price.currency.currencyCode;
    this.transactionMainProductTax = `${room.price.tax}`;
    this.transactionMainProductPrice = `${room.price.value}`;
  }

  addTransactionId(id): void {
    this.transactionId = id;
  }

  setTransactionTotal(total): void {
    this.transactionTotal = total;
  }

  setCulture(culture): void {
    this.culture = culture;
  }

  setMainProductName(paramName): void {
    switch (paramName) {
      case 'meetingRoom':
        this.transactionMainProductName = 'Meeting Room';
        this.transactionMainProductUrlPart = 'meeting-room';
        break;
      case 'trainingRoom':
        this.transactionMainProductName = 'Training Room';
        this.transactionMainProductUrlPart = 'training-room';
        break;
      case 'dayOffice':
        this.transactionMainProductName = 'Day Office';
        this.transactionMainProductUrlPart = 'day-office';
        break;
      case 'dayCoworking':
        this.transactionMainProductName = 'Day Co-working';
        this.transactionMainProductUrlPart = 'day-coworking';
        break;
      default:
        this.transactionMainProductName = 'Meeting Room';
        this.transactionMainProductUrlPart = 'meeting-room';
        break;
    }
  }

  resetTransactionProductsData(): void {
    this.transactionProducts = [];
  }

  // track methods push to dataLayer
  trackGeneralSiteData(params: any): void {
    this.dataLayer.push({
      Country: `${params.countryName}`,
      City: '',
      'Customer Type': '',
      Language: `${params.locale}`,
      Section: 'MeetingRoomCheckout',
      SubSection: ''
    });
  }

  trackEcommerceStep(title: string, url: string): void {
    this.dataLayer.push({
      event: 'VirtualPageview',
      virtualPageURL: `${url}`,
      virtualPageTitle: title
    });
  }

  trackEcommerceEvent(event, label): void {
    this.dataLayer.push({
      event: event,
      eventCategory: 'Button',
      eventAction: 'Click',
      eventLabel: label
    });
  }

  trackSearchEvent(searchTerm: string): void {
    this.dataLayer.push({
      event: 'searchTermPageView',
      virtualPageTitle: 'Choose a meeting room',
      virtualPageURL: `/search_query?q=${searchTerm}&category=undefined`
    });
  }

  trackTransactionData(): void {
    this.dataLayer.push({
      event: 'trackTrans',
      transactionId: `${this.transactionId}`,
      transactionAffiliation: `${this.hostname}`,
      transactionTotal: `${this.transactionTotal}`,
      transactionTax: `${this.transactionMainProductTax}`,
      transactionShipping: '0',
      transactioncurrencyCode: `${this.transactionCurrencyCode}`,
      transactionProducts: this.transactionProducts
    });
  }

  trackDataLayerStartBooking(): void {
    this.dataLayer.push({
      data: {
        // page_title: `Checkout ${this.transactionMainProductName} - Start Booking`,
        page_title: `Checkout  Meeting Room - Start Booking`,
        // eslint-disable-next-line max-len
        page_path: `/${this.urlParamsService.queryParams.locale.toLowerCase()}/checkout/${this.transactionMainProductUrlPart}/start-booking`
      },
      _clear: true,
      event: 'checkout_view'
    });
  }

  trackDataLayerChooseRoom({ date, startTime, endTime, capacity, location, city, country }: DataLayerTracker): void {
    this.dataLayer.push({
      data: {
        // page_title: `Checkout ${this.transactionMainProductName} - Choose Room`,
        page_title: `Checkout Meeting Room - Choose Room`,
        page_path: `/${this.urlParamsService.queryParams.locale.toLowerCase()}/checkout/${this.transactionMainProductUrlPart}/choose-room`,
        space_type: this.transactionMainProductName,
        date,
        start_time: startTime,
        end_time: endTime,
        capacity,
        location,
        city,
        country
      },
      _clear: true,
      event: 'checkout_view'
    });
  }

  trackDataLayerBookingDetails({
    date,
    startTime,
    endTime,
    capacity,
    location,
    city,
    country,
    centreName,
    centreNumber,
    price,
    currency
  }: DataLayerTracker): void {
    this.dataLayer.push({
      data: {
        // page_title: `Checkout ${this.transactionMainProductName} - Booking Details`,
        page_title: `Checkout Meeting Room - Booking Details`,
        // eslint-disable-next-line max-len
        page_path: `/${this.urlParamsService.queryParams.locale.toLowerCase()}/checkout/${this.transactionMainProductUrlPart}/booking-details`,
        space_type: this.transactionMainProductName,
        date,
        start_time: startTime,
        end_time: endTime,
        capacity,
        location,
        city,
        country,
        centre: centreName,
        centre_id: centreNumber.toString(),
        price: price.toString(),
        currency
      },
      _clear: true,
      event: 'checkout_view'
    });
  }

  trackRegistrationAttempt(): void {
    this.dataLayer.push({
      data: {
        status: 'Submit Attempt'
      },
      _clear: true,
      event: 'registration'
    });
  }

  trackRegistrationFail(errorMessage: string): void {
    this.dataLayer.push({
      data: {
        status: 'Submit Attempt',
        form_field: '',
        message: errorMessage
      },
      _clear: true,
      event: 'registration'
    });
  }

  trackRegistrationSuccess(): void {
    this.dataLayer.push({
      data: {
        status: 'Submit Success'
      },
      _clear: true,
      event: 'registration'
    });
  }

  trackDataLayerPayment({
    date,
    startTime,
    endTime,
    capacity,
    location,
    city,
    country,
    centreName,
    centreNumber,
    price,
    currency
  }: DataLayerTracker): void {
    this.dataLayer.push({
      data: {
        page_title: `Checkout ${this.transactionMainProductName} - Payment`,
        page_path: `/${this.urlParamsService.queryParams.locale.toLowerCase()}/checkout/${this.transactionMainProductUrlPart}/payment`,
        space_type: this.transactionMainProductName,
        date,
        start_time: startTime,
        end_time: endTime,
        capacity,
        location,
        city,
        country,
        centre: centreName,
        centre_id: centreNumber.toString(),
        price: price.toString(),
        currency
      },
      _clear: true,
      event: 'checkout_view'
    });
  }

  trackDataLayerPurchase(data): void {
    this.dataLayer.push({
      data: {
        page_title: `Checkout ${this.transactionMainProductName} - Purchase`,
        page_path: ``,
        ...data
      },
      _clear: true,
      event: 'checkout_view'
    });
  }

  trackUpdatedUser(userId: string): void {
    this.dataLayerRegus.push(Object.assign({}, this.trackingUser, { userId }));
  }

  initGTMTracking(): void {
    this.dataLayerRegus.push({
      'gtm.start': Date.now(),
      event: 'gtm.js'
    });
  }

  trackUser(
    userId: string | undefined,
    country: string,
    pageGroup: string,
    language: string,
    websiteVersion: string
  ): void {
    this.dataLayerRegus.push({
      country,
      pageGroup,
      userId,
      language,
      websiteVersion
    });
    this.trackingUser = { country, pageGroup, language, websiteVersion };
  }

  trackCompleteBooking(
    eventCategory: string,
    eventAction: string,
    eventLabel: string,
    eventNonInteraction: string,
    userEcommStatus: string,
    event,
    ecommerce: object,
    pageGroup = 'e-commerce'
  ): void {
    this.dataLayerRegus.push({
      eventCategory,
      eventAction,
      eventLabel,
      eventNonInteraction,
      userEcommStatus,
      event,
      ecommerce,
      pageGroup
    });
    this.dataLayerRegus.push({
      element: 'form',
      context: 'payment',
      item: undefined,
      action: 'validation',
      name: undefined,
      event: 'formEvent'
    });
  }

  trackTransaction(data, locationName): void {
    const transactionEvent = {
      event: 'transaction',
      ecommerce: {
        currencyCode: data.currencyCode,
        purchase: data.purchase,
        locationName: locationName,
        agreementLength: undefined,
        startDate: undefined,
        user_data: data.user_data
      }
    };
    this.dataLayer.push(transactionEvent);
  }

  trackEvent(event: object): void {
    this.dataLayerRegus.push(event);
  }

  trackCheckoutStep(serviceTitle: string, stepTitle: string, stepUrl: string | boolean = false): void {
    const title2url = (title: string): string => title.toLowerCase().replaceAll(' ', '-');
    this.dataLayerRegus.push({
      virtualPageTitle: `Checkout: ${serviceTitle} - ${stepTitle}`,
      virtualPageUrl: `/${title2url(serviceTitle)}-booking/checkout/${stepUrl || title2url(stepTitle)}`,
      event: 'virtualPageview'
    });
  }

  trackDecibelInsight(event: string, dataString: string): void {
    if (this.decibelInsight) {
      this.decibelInsight(event, dataString);
    }
  }

  trackUserNavigation(stepId: number): void {
    this.http.post(`${environment.apiUrl}navigationTracking?flowStepId=${stepId}`, {}).subscribe();
  }
}
